import FontIcon from 'components/icons/FontIcon';
import { TextButton } from 'components/v2/buttons';
import { confirmDelete } from 'components/v2/confirm-dialog/confirmDelete';
import CompilerSelectControlledComponent from 'components/v2/epd-compiler-inputs/CompilerSelectControlledComponent';
import WizardAddButton from 'components/v2/epd-wizard-inputs/WizardAddButton';
import WizardTextInputComponent from 'components/v2/epd-wizard-inputs/WizardTextInput';
import { HelpBox } from 'components/v2/help-boxes';
import TooltipErrorIcon from 'components/v2/icons/TooltipErrorIcon';
import TooltipHelpIcon from 'components/v2/icons/TooltipHelpIcon';
import React, { useState } from 'react';
import { CPCModel, ProductClassificationModel } from 'services/EpdClient';
import { EPD_DICTIONARY_CODES } from 'services/api/constants';
import {
  useCreateProductClassification,
  useDeleteProductClassification,
  useUpdateProductClassification,
} from 'services/api/mutations';
import { useDictionaryValues, useUnCpcCodes } from 'services/api/queries';
import styled from 'styled-components';
import {
  ExpandablePanelHeaderName,
  ExpandablePanelModule,
  HeaderCaptionSemiBold,
  HorizontalDivider,
} from 'styles/v2/Styles.styled';
import { EPDProductInformationModel, Option } from 'types/types';
import { getOptionsFromDictionary } from 'util/utils';

import ToggleButton from '../../panels/toggleable/ToggleButton';
import useIsReadOnlyMode from '../../hooks/useIsReadOnlyMode';
import WizardCheckbox from 'components/v2/epd-wizard-inputs/WizardCheckbox';
import { TooltipPosition } from 'components/controls/Tooltip';

const ProductClassificationSection: React.FunctionComponent<{
  product?: EPDProductInformationModel;
  onChange: any;
  errors: any;
}> = ({ product, onChange, errors }) => {
  const [rowValues, setRowValues] = useState<any[]>([]);
  const createMutation = useCreateProductClassification();
  const deleteMutation = useDeleteProductClassification();
  const updateMutation = useUpdateProductClassification();
  const isReadOnly = useIsReadOnlyMode();

  const cpcCodes = useUnCpcCodes({ smartFilter: undefined, cpcLevel: undefined, codes: undefined }).data;
  const productDetailsErrors = errors?.productClassificationSystemBlock;

  function noErrorsAtProductClassificationSystemBlock(errors: any): boolean | undefined {
    const fieldNames: string[] = ['productClassificationSystemBlock'];

    return !errors || !fieldNames.some((field) => field in errors);
  }

  const selectedCpc = () => {
    if (product?.uncpcId) {
      return {
        value: product?.uncpcId,
        label: cpcCodes?.find((cpc: any) => cpc?.id === product?.uncpcId)?.description,
      } as Option;
    }
    return null;
  };

  const confirmDeleteClassification = (item: any) => {
    confirmDelete({
      caption: `Are you sure you want to delete product classification?`,
      onConfirm: () => deleteMutation.mutate(item.id),
    });
  };

  const dictionariesQuery = useDictionaryValues();

  const productClassificationOptions = getOptionsFromDictionary(
    dictionariesQuery.data,
    EPD_DICTIONARY_CODES.CLASSIFICATION_CODE
  );

  const createRow = () => {
    createMutation.mutate({ productId: product?.id! });
    const newRowValues = [...rowValues, { id: '', name: '', value: '' }];
    setRowValues(newRowValues);
  };

  const headerModuleTemplate = (
    options: any,
    header: string,
    tooltip?: string,
    tooltipPosition?: TooltipPosition,
    required?: boolean,
    isFilledIn?: boolean
  ) => {
    return (
      <div className={`${options.className} ${options.collapsed ? 'header-collapsed' : ''}`}>
        <ExpandablePanelHeaderName>
          <HeaderCaptionSemiBold>{header}</HeaderCaptionSemiBold>
          {tooltip && <TooltipHelpIcon content={tooltip} position={tooltipPosition} />}
          {required && !isFilledIn && <TooltipErrorIcon />}
          <ToggleButton options={options} />
        </ExpandablePanelHeaderName>
      </div>
    );
  };

  const onChangeInternal = (item: ProductClassificationModel, name: keyof ProductClassificationModel, value: any) => {
    item[name] = value;
    updateMutation.mutate(item);
  };

  return (
    <ExpandablePanelModule
      headerTemplate={(options) =>
        headerModuleTemplate(
          options,
          'Product classification system',
          'Other relevant codes for product classification may also be included, e.g.\n- Common Procurement Vocabulary (CPV).\n- United Nations Standard Products and Services Code® (UNSPSC).\n- Classification of Products by Activity (NACE/CPA).\n- Australian and New Zealand Standard Industrial Classification (ANZSIC).',
          'right',
          true,
          noErrorsAtProductClassificationSystemBlock(errors)
        )
      }
      toggleable
    >
      <div>
        <CompilerSelectControlledComponent
          label="UN CPC Code"
          name="UNCPCCode"
          tooltip="United Nations Central Product Classification code (based on v.2.1)"
          tooltipPosition={'right'}
          options={cpcCodes?.map((item: CPCModel) => {
            return {
              value: item?.id,
              label: item?.description,
            } as Option;
          })}
          value={selectedCpc()}
          onChanged={onChange}
          placeholder="Select"
          error={productDetailsErrors?.code}
          disabled={!!product?.isUNCPCCodeNotApplicable}
        />
        <CheckBoxGroup>
          <WizardCheckbox
            inputId="isUNCPCCodeNotApplicable"
            label="No applicable UN CPC code"
            name="isUNCPCCodeNotApplicable"
            value={product?.isUNCPCCodeNotApplicable || false}
            onChanged={() => onChange('isUNCPCCodeNotApplicable', !product?.isUNCPCCodeNotApplicable)}
            disabled={isReadOnly}
            size="small"
          />
        </CheckBoxGroup>
      </div>

      <WizardTextInputComponent
        label="GTIN"
        tooltip="Global trade item number."
        tooltipPosition={'right'}
        placeholder="Type here"
        value={product?.gtin ?? ''}
        onChanged={onChange}
        name="GTIN"
        error={productDetailsErrors?.gtin}
      />

      <div>
        <HelpBox>
          <span>Declaration of other product classification systems</span>
        </HelpBox>
        <WizardAddButton
          label="+ Add product classification system"
          onClick={createRow}
          disabled={(product?.productClassifications?.length as number) >= 5}
        />
      </div>

      {product?.productClassifications?.map((item, index) => {
        const selected = productClassificationOptions.find(
          (x) => x.value.toString() === item?.productClassificationType?.toString()
        );
        return (
          <React.Fragment key={item.id}>
            {index > 0 && <HorizontalDivider style={{ margin: '0px' }} />}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', rowGap: '30px' }}>
              <div style={{ display: 'grid', rowGap: '30px' }}>
                <CompilerSelectControlledComponent
                  label="Product classification system"
                  name="productClassificationType"
                  options={productClassificationOptions}
                  value={selected ? { value: selected.value.toString(), label: selected.label } : undefined}
                  onChanged={(name: any, value: any) => {
                    onChangeInternal(item, name, value);
                  }}
                  placeholder="Select"
                />

                {productClassificationOptions.find((x) => x.value == item?.productClassificationType?.toString())?.label ===
                  'Other' && (
                  <WizardTextInputComponent
                    label="Product classification system name"
                    onChanged={(name: any, value: any) => {
                      onChangeInternal(item, name, value);
                    }}
                    placeholder="Type here"
                    name="description"
                    value={item?.description}
                  />
                )}
                <WizardTextInputComponent
                  label="Code"
                  placeholder="Type here"
                  value={item.code}
                  onChanged={(name: any, value: any) => {
                    onChangeInternal(item, name, value);
                  }}
                  name="code"
                />
              </div>
              {!isReadOnly && (
                <TextButton
                  style={{ alignSelf: 'flex-start', margin: '1.5rem 0 0 1rem', width: 'auto' }}
                  type="button"
                  text
                  icon={<FontIcon icon="delete" />}
                  onClick={() => confirmDeleteClassification(item)}
                />
              )}
            </div>
          </React.Fragment>
        );
      })}
    </ExpandablePanelModule>
  );
};

const CheckBoxGroup = styled.div`
  text-align: start;
  margin-top: 0.5rem;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  border-radius: 2px;
`;

export default ProductClassificationSection;
